import axios, { AxiosError, AxiosResponse } from "axios";
import { CONSTANTS } from "./constants";
import { getTokenFromLocalStorage } from "@/utils/v2";
import { useUsersStore } from "@/stores/v2";

export interface ApiError {
  message: string;
  status?: number | string;
  data?: any;
  errors?: any;
}

interface ApiErrorResponse {
  message: string;
  [key: string]: unknown;
}

const isApiErrorResponse = (data: unknown): data is ApiErrorResponse => {
  return typeof data === "object" && data !== null && "message" in data;
};

export const ApiClient = axios.create({
  baseURL: CONSTANTS.BACKEND_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

let isRedirecting = false;

ApiClient.interceptors.request.use(
  (config) => {
    const token = getTokenFromLocalStorage();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError): Promise<ApiError> => {
    return Promise.reject(handleError(error));
  }
);

ApiClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    if (error.response && (
      error.response.status === 401 ||
      (error.response.data &&
        isApiErrorResponse(error.response.data) &&
        error.response.data.message.toLowerCase().includes("unauthenticated"))
    )) {
      localStorage.removeItem("token");

      try {
        const userStore = useUsersStore();
        userStore.logout();
      } catch (e) {
        console.error("Could not reset user state:", e);
      }

      if (!isRedirecting) {
        isRedirecting = true;
        window.location.href = "/";
      }

      return Promise.resolve({
        data: {
          data: [{
            id: null,
            name: null,
            sites: []
          }]
        },
        status: 200,
        statusText: "OK",
        headers: {},
        config: {},
      } as AxiosResponse);
    }

    return Promise.reject(handleError(error));
  }
);

interface ApiErrorWithStatus extends Error {
  status?: number;
  data?: unknown;
}

const handleError = (error: AxiosError): ApiErrorWithStatus => {
  if (error.response) {
    const status = error.response.status;
    const errorData = error.response.data;

    const message = isApiErrorResponse(errorData)
      ? errorData.message
      : "An error occurred";

    const err = new Error(message) as ApiErrorWithStatus;
    err.status = status;
    err.data = errorData;

    return err;
  }

  if (error.request) {
    return new Error("No response received from server") as ApiErrorWithStatus;
  }

  return new Error(error.message || "An unexpected error occurred") as ApiErrorWithStatus;
};